import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, SITE_ORIGIN } from "@/lib/api";
import SellerAvatar from "@/components/SellerAvatar";
import { CalendarIcon, ArrowRightIcon } from "@phosphor-icons/react";

const fileUrl = (path) =>
  path && path.startsWith("http")
    ? path
    : path
    ? `${SITE_ORIGIN}/api/files/${path}`
    : null;

function relativeWhen(iso) {
  if (!iso) return "Soon";
  const then = new Date(iso);
  const now = new Date();
  const diffMs = then - now;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 0) return "Starting now";
  if (diffMin < 60) return `in ${diffMin}m`;
  if (diffMin < 24 * 60) return `in ${Math.round(diffMin / 60)}h`;
  const days = Math.round(diffMin / (24 * 60));
  return `in ${days}d`;
}

export default function UpcomingStreams() {
  const [streams, setStreams] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/streams", {
          params: { status: "scheduled", limit: 8 },
        });
        const items = Array.isArray(data) ? data : data.items || [];
        if (alive) setStreams(items.slice(0, 8));
      } catch {
        if (alive) setStreams([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Hide the section when there are no scheduled streams yet — better to
  // show nothing than an awkward empty grid on a fresh deploy.
  if (streams !== null && streams.length === 0) return null;

  return (
    <section
      className="bg-white border-b border-zinc-200"
      data-testid="home-upcoming-streams"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#1D78AB] mb-3">
              <CalendarIcon size={16} weight="fill" /> On the schedule
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter max-w-xl leading-tight">
              Coming up live this week.
            </h2>
          </div>
          <Link
            to="/live"
            className="text-sm font-semibold text-[#1D78AB] hover:underline"
            data-testid="home-upcoming-all"
          >
            Full schedule <ArrowRightIcon size={14} className="inline ml-1" />
          </Link>
        </div>

        {!streams ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl bg-zinc-100 h-40" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {streams.map((s) => (
              <Link
                key={s.id}
                to={`/stream/${s.id}`}
                className="group rounded-2xl border border-zinc-200 bg-white overflow-hidden hover:border-[#1D78AB] hover:shadow-lg transition"
                data-testid={`upcoming-stream-${s.id}`}
              >
                <div className="aspect-video bg-zinc-100 relative overflow-hidden">
                  {s.cover_image ? (
                    <img
                      src={fileUrl(s.cover_image)}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1D78AB] to-zinc-900" />
                  )}
                  <div className="absolute top-2 left-2 pill bg-amber-400 text-zinc-900 text-[10px] font-bold">
                    {relativeWhen(s.scheduled_at)}
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm text-zinc-900 line-clamp-2 min-h-[2.5em]">
                    {s.title || "Untitled stream"}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <SellerAvatar
                      name={s.seller_name}
                      picture={s.seller_picture}
                      size={22}
                    />
                    <div className="text-xs text-zinc-500 truncate">
                      {s.seller_name || "Bidorah seller"}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
